import { Navigate, useParams } from 'react-router'

import { OlarkHelper } from '@fv/client-components'
import type { Workflow } from '@fv/models'

import { Loading } from '@/components/Loading'
import { ModeNav } from '@/components/ModeNav'
import { ShipmentTemplateSelector } from '@/features/load-templates/ShipmentTemplateSelector'
import { routes } from '@/routes'

import { useWorkflowEnabled } from '../auth/HasWorkflow'
import { useDefaultWorkflow } from '../profiile/hooks'
import { useUnifiedShipmentFlow } from '../route-guides/hooks'
import { QuotePageForm } from './QuotePageForm'
import { QuotePageProvider, useQuotePageCtx } from './QuotePageProvider'

type Params = {
  loadId: string
  mode: string
  workflow: string
}

export const QuotePage = () => {
  const { loadId = '', mode, workflow: workflowParam } = useParams<Params>()
  const workflow = workflowParam as Workflow
  const isCreate = !mode && !loadId
  const validMode = isCreate || mode === 'copy' || mode === 'edit'
  const validWorkflow = workflow === 'ltl' || workflow === 'parcel'
  const unifiedFlow = useUnifiedShipmentFlow()
  const workflowEnabled = useWorkflowEnabled(workflow)
  const defaultWorkflow = useDefaultWorkflow()

  if (unifiedFlow.isLoading) return <Loading />

  if (unifiedFlow.isEnabled(workflow)) {
    const unifiedRoute = isCreate
      ? routes.create()
      : mode === 'copy'
        ? routes.copyLoad(loadId)
        : routes.editLoad(loadId)
    return <Navigate to={unifiedRoute} replace />
  }

  if (!workflowEnabled) {
    return <Navigate to={routes.quote(defaultWorkflow)} replace />
  }

  if (!validMode && validWorkflow) {
    return (
      <Navigate
        to={routes.quote(workflow, { id: loadId, mode: 'copy' })}
        replace
      />
    )
  }
  if (!validWorkflow) {
    return <Navigate to={routes.quote('truckload')} replace />
  }

  return (
    <QuotePageProvider key={workflow} workflow={workflow}>
      <QuotePageContents loadId={loadId} isCreate={isCreate} />
    </QuotePageProvider>
  )
}

const QuotePageContents = ({
  loadId,
  isCreate,
}: {
  loadId: string
  isCreate: boolean
}) => {
  const workflow = useQuotePageCtx(s => s.load.workflow)
  const selectedTemplate = useQuotePageCtx(s => s.selectedTemplate)
  const applyLoadTemplate = useQuotePageCtx(s => s.actions.applyLoadTemplate)
  const clearLoadTemplate = useQuotePageCtx(s => s.actions.clearLoadTemplate)

  return (
    <div className="shipper-page-fill shipper-grid-rows-single-fill grid grid-cols-[15rem_1fr] bg-[#f2f2f2] b1200:block b1200:bg-white b1350:grid-cols-[1fr] b1350:grid-rows-[auto_minmax(0,1fr)] b1300:grid-cols-1">
      <ModeNav className="min-h-0">
        {isCreate && (
          <ShipmentTemplateSelector
            selectedTemplate={selectedTemplate}
            workflow={workflow}
            onSelect={applyLoadTemplate}
            onClear={clearLoadTemplate}
          />
        )}
      </ModeNav>

      <div className="flex h-full min-h-0 min-w-0 flex-col">
        <QuotePageForm />
      </div>

      <OlarkHelper loadId={loadId} />
    </div>
  )
}
